import { Component, inject } from '@angular/core';
import { FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';

import { LocaleService } from '@next/core-lib/i18n';
import { startWith, filter, map } from 'rxjs/operators';
import { SoilType } from 'src/app/soil-type/soil-type.model';

import { Crop, provinces, Template, TemplateType } from '../../models';

export interface CreateTemplateDialogData extends AbstractFormDialogData {
  crops: Crop[];
  soilTypes: SoilType[];
  cropYears: number[];
}

@Component({
  selector: 'app-create-template-dialog',
  templateUrl: './create-template-dialog.component.html',
  standalone: false,
})
export class CreateTemplateDialogComponent extends AbstractFormDialog<Template> {
  data = inject<CreateTemplateDialogData>(MAT_DIALOG_DATA);
  private readonly locale = inject(LocaleService);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
    inject<MatDialogRef<CreateTemplateDialogComponent, AbstractDialogResultData<Template>>>(
      MatDialogRef,
    );

  readonly provinces = provinces;

  templateTypes = [
    { value: TemplateType.GBM, text: this.locale.translate(TemplateType.GBM) },
    {
      value: TemplateType.Fertilization,
      text: this.locale.translate(TemplateType.Fertilization),
    },
  ];

  formGroup = this.formBuilder.group({
    templateType: [undefined, Validators.required],
    cropYear: [undefined, Validators.required],
    cropId: [undefined, Validators.required],
    soilTypeId: [undefined, Validators.required],
    isSkal: [false],
    isPlanetProof: [false],
    isGroundWaterPreservation: [false],
    province: [[], [Validators.required, Validators.minLength(1)]],
  });

  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filter(value, [...this.data.crops])),
  );

  get provinceControl(): FormControl {
    return this.formGroup.get('province') as FormControl;
  }

  displayCrop() {
    return (cropId?: number) =>
      cropId ? this.data.crops.find((crop) => crop.id === cropId)?.name || '' : '';
  }

  onProvinceSelectionChange(event: MatSelectChange): void {
    this.formGroup.controls.province.setValue(event.value ?? []);
  }

  private filter(value: string, items: Crop[]) {
    return items
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => option.name.toLowerCase().includes(value.toLowerCase()));
  }
}
